import axios from 'axios';
import { eventService } from './eventService';

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.utiliti.ng";


// Helper function to handle API responses
const handleResponse = async (response) => {
  try {
    const data = await response.json();
    if (!response.ok) {
      console.error('API Error:', {
        status: response.status,
        statusText: response.statusText,
        data: data
      });
      throw new Error(data.message || 'API request failed');
    }
    return data;
  } catch (error) {
    console.error('Response handling error:', error);
    throw error;
  }
};

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:4000',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for authentication
axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Handle unauthorized access
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const api = {
  // Auth endpoints body: JSON.stringify({ pin })
  auth: {
    login: async (credentials) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/loginAdmin`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: JSON.stringify(credentials),
        });
        const data = await response.json();
        if (!response.ok) {
          throw data;
        }
        return {
          status: 'success',
          data: data,
        };
      } catch (error) {
        console.error('Login error:', error);
        throw error;
      }
    },
    verifyPin: async (pin, token) => {
      try {
        console.log('Verifying PIN with token:', token);
        const response = await fetch(`${API_BASE_URL}/admin/verify-pin`, {
          method: 'POST',
          headers: {
            'x-auth-token': token,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ pin })
        });
        const data = await response.json();
        if (!response.ok) {
          console.error('PIN verification failed:', data);
          throw data;
        }
        return {
          status: 'success',
          data: data,
        };
      } catch (error) {
        console.error('Verify PIN error:', error);
        throw error;
      }
    },

    getProfile: async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('No token found');
        }
        const response = await fetch(`${API_BASE_URL}/admin/getProfile`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });

        const data = await response.json();
        if (!response.ok) {
          throw data;
        }
        return data;
      } catch (error) {
        console.error('Get profile error:', error);
        throw error;
      }
    },

    // ...other auth methods (register, logout, etc.)
  },

  // Example: Dashboard endpoints
  dashboard: {
    getTotalUsers: async (month, year) => {
      try {
        const params = new URLSearchParams();
        if (year) {
          params.append('year', year);
          if (month) {
            params.append('month', month);
          }
        }
        const response = await fetch(`${API_BASE_URL}/admin/getTotalNumberOfAllUsers?${params.toString()}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data || 0;
      } catch (error) {
        console.error('Get total users error:', error);
        return 0;
      }
    },
    getTotalBalance: async (month, year) => {
      try {
        const params = new URLSearchParams();
        if (year) {
          params.append('year', year);
          if (month) {
            params.append('month', month);
          }
        }
        const response = await fetch(`${API_BASE_URL}/admin/getTotalUserBalance?${params.toString()}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        // Ensure we return a number
        return typeof data === 'number' ? data : Number(data) || 0;
      } catch (error) {
        console.error('Get total balance error:', error);
        return 0;
      }
    },
    getTotalTransactions: async (month, year) => {
      try {
        const params = new URLSearchParams();
        if (year) {
          params.append('year', year);
          if (month) {
            params.append('month', month);
          }
        }
        const response = await fetch(`${API_BASE_URL}/admin/getTotalNumberOfTransactions?${params.toString()}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data || 0;
      } catch (error) {
        console.error('Get total transactions error:', error);
        return 0;
      }
    },
    getAllChats: async (month, year) => {
      try {
        const params = new URLSearchParams();
        if (year) {
          params.append('year', year);
          if (month) {
            params.append('month', month);
          }
        }
        const response = await fetch(`${API_BASE_URL}/admin/getAllChats?${params.toString()}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data || [];
      } catch (error) {
        console.error('Get all chats error:', error);
        return [];
      }
    },
    getAllUsers: async (limit) => {
      try {
        const url = limit
          ? `${API_BASE_URL}/admin/getAllUsers?limit=${limit}`
          : `${API_BASE_URL}/admin/getAllUsers`;

        const response = await fetch(url, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data || [];
      } catch (error) {
        console.error('Get all users error:', error);
        return [];
      }
    },
    getUserById: async (userId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/getUserById/${userId}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data.data || data; // Return the user data
      } catch (error) {
        console.error('Get user by ID error:', error);
        throw error;
      }
    },
    getOnlineUsers: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/online-users`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        // Handle the nested structure: { success: true, data: { onlineUsers: [...], totalOnline: 1 } }
        return data.data?.onlineUsers || data.onlineUsers || data.data || data || [];
      } catch (error) {
        console.error('Get online users error:', error);
        return [];
      }
    },
    getOnlineAdmins: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/online-admins`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data.data?.onlineAdmins || data.onlineAdmins || data.data || data || [];
      } catch (error) {
        console.error('Get online admins error:', error);
        return [];
      }
    },
    heartbeat: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/heartbeat`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ status: 'online' }),
        });
        return await handleResponse(response);
      } catch (error) {
        console.error('Heartbeat error:', error);
        throw error;
      }
    },
    cleanupOfflineUsers: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/user/cleanup-offline-users`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data;
      } catch (error) {
        console.error('Cleanup offline users error:', error);
        throw error;
      }
    },
    updateLastActive: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/update-last-active`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data;
      } catch (error) {
        console.error('Update last active error:', error);
        return null;
      }
    },
    getAllTransactions: async (params = {}) => {
      const {
        page = 1,
        limit = 50,
        search = '',
        status = 'all',
        trnxType = 'all',
        purpose = 'all',
        startDate = '',
        endDate = '',
        includeStats // Add this
      } = params;

      const queryParams = new URLSearchParams({
        page,
        limit,
        search,
        status,
        trnxType,
        purpose,
        startDate,
        endDate
      });

      if (includeStats !== undefined) {
        queryParams.append('includeStats', includeStats);
      }

      const queryString = queryParams.toString();

      const response = await fetch(`${API_BASE_URL}/admin/getAllTransactions?${queryString}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getDeletedTransactions: async () => {
      const response = await fetch(`${API_BASE_URL}/admin/getDeletedTransactions`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    deleteTransactions: async (transactionIds) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/deleteTransactions`, {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ transactionIds })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Delete transactions error:', error);
        throw error;
      }
    },
    reverseTransaction: async (transactionId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/reverseTransaction`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ transactionId })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Reverse transaction error:', error);
        throw error;
      }
    },
    getTransactionPurposeStats: async (params = {}) => {
      // Build query string from params object
      const query = new URLSearchParams(params).toString();
      const response = await fetch(`${API_BASE_URL}/admin/getTransactionPurposeStats?${query}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getAllUsersTransactions: async (year, month, compare = '') => {
      const response = await fetch(`${API_BASE_URL}/admin/getTransactionHistory?year=${year}&month=${month}${compare ? `&compare=${compare}` : ''}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getPayscribeBalance: async () => {
      const response = await fetch(`${API_BASE_URL}/admin/getPayscribeBalance`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getPayscribeProfit: async (month, year) => {
      const params = new URLSearchParams();
      if (year) {
        params.append('year', year);
        if (month) {
          params.append('month', month);
        }
      }
      const response = await fetch(`${API_BASE_URL}/admin/payscribe-profit?${params.toString()}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getSafeHavenProfit: async (month, year) => {
      const params = new URLSearchParams();
      if (year) {
        params.append('year', year);
        if (month) {
          params.append('month', month);
        }
      }
      const response = await fetch(`${API_BASE_URL}/admin/safehaven-profit?${params.toString()}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getEarnings: async (month, year, day, filter) => {
      try {
        const params = new URLSearchParams();
        if (filter) {
          params.append('filter', filter);
        } else {
          if (year) {
            params.append('year', year);
            if (month) {
              params.append('month', month);
              if (day) {
                params.append('day', day);
              }
            }
          }
        }
        const url = `${API_BASE_URL}/admin/earnings${params.toString() ? '?' + params.toString() : ''}`;
        console.log('Fetching earnings from:', url);
        const response = await fetch(url, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        console.log('Earnings response status:', response.status);
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }
        return handleResponse(response);
      } catch (error) {
        console.error('Error in getEarnings:', error);
        throw error;
      }
    },
    backupDatabase: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/backup-database`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Backup failed');
        }

        // Get the filename from the response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `database_backup_${new Date().toISOString().split('T')[0]}_${Date.now()}.json`;

        // Get the JSON data
        const data = await response.json();

        // Create and download the file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, filename, message: 'Database backup downloaded successfully' };
      } catch (error) {
        console.error('Backup database error:', error);
        throw error;
      }
    },
    backupDatabase: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/backup-database`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Backup failed');
        }

        // Get the filename from the response headers
        const contentDisposition = response.headers.get('Content-Disposition');
        const filename = contentDisposition
          ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
          : `database_backup_${new Date().toISOString().split('T')[0]}_${Date.now()}.json`;

        // Get the JSON data
        const data = await response.json();

        // Create and download the file
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        return { success: true, filename, message: 'Database backup downloaded successfully' };
      } catch (error) {
        console.error('Backup database error:', error);
        throw error;
      }
    },
    getSafeHavenBalance: async () => {
      const response = await fetch(`${API_BASE_URL}/admin/getSafeHavenBalance`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    getSystemWalletBalance: async () => {
      const response = await fetch(`${API_BASE_URL}/admin/getSystemWalletBalance`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },
    fundSystemWallet: async (data) => {
      const response = await fetch(`${API_BASE_URL}/admin/fund-system-wallet`, {
        method: 'POST',
        headers: api.getAuthHeaders(),
        body: JSON.stringify(data)
      });
      return handleResponse(response);
    },
    getTotalSales: async (month, year) => {
      const params = new URLSearchParams();
      if (year) {
        params.append('year', year);
        if (month) {
          params.append('month', month);
        }
      }
      const response = await fetch(`${API_BASE_URL}/admin/getTotalSales?${params.toString()}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    },

    // Funding Approval Management
    getFundingRequests: async (status = 'pending') => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/funding-approvals?status=${status}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get funding requests error:', error);
        throw error;
      }
    },

    approveFundingRequest: async (requestId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/funding-approvals/${requestId}/approve`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Approve funding request error:', error);
        throw error;
      }
    },

    rejectFundingRequest: async (requestId, reason) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/funding-approvals/${requestId}/reject`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ reason })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Reject funding request error:', error);
        throw error;
      }
    },
    getAllAdmins: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/getAllAdmins`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return data || [];
      } catch (error) {
        console.error('Get all admins error:', error);
        return [];
      }
    },
    updateAdminRole: async (adminId, newRole) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/updaterole/${adminId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ role: newRole })
        });
        const data = await handleResponse(response);
        return data;
      } catch (error) {
        console.error('Update admin role error:', error);
        throw error;
      }
    },
    // Permission management endpoints
    getAdminPermissions: async (username) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/permissions/${username}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get admin permissions error:', error);
        throw error;
      }
    },
    updateAdminPermissions: async (username, permissions) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/permissions/${username}`, {
          method: 'PUT',
          headers: {
            ...api.getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ permissions })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Update admin permissions error:', error);
        throw error;
      }
    },
    getAllPermissions: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/permissions`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get all permissions error:', error);
        throw error;
      }
    },
    makeUserAdmin: async (userId, role) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/makeuseradmin/${userId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ role })
        });
        const data = await handleResponse(response);
        return data;
      } catch (error) {
        console.error('Make user admin error:', error);
        throw error;
      }
    },
    updateUserStatus: async (userId, status) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/updateUserStatus/${userId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ status })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Update user status error:', error);
        throw error;
      }
    },
    sendNotification: async (userId, title, content) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/sendNotification/${userId}`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ title, content })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Send notification error:', error);
        throw error;
      }
    },
    // Verify admin PIN
    verifyAdminPin: async (pin) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/verify-pin`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ pin })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Verify PIN error:', error);
        throw error;
      }
    },
    // Update user wallet balance
    updateUserWallet: async (userId, data) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/funduser/${userId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(data)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Update user wallet error:', error);
        throw error;
      }
    },
    bulkFundWallets: async (bulkFundData, otp) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/bulkFundWallets`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ bulkFundData, otp })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Bulk fund wallets error:', error);
        throw error;
      }
    },
    // Log admin activity
    logAdminActivity: async (activity) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/activities`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(activity)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Log admin activity error:', error);
        throw error;
      }
    },
    // Get admin activities
    getAdminActivities: async (params = {}) => {
      try {
        // Construct query parameters including the new filters
        const queryParams = new URLSearchParams();

        // Add all existing params
        Object.keys(params).forEach(key => {
          if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
            queryParams.append(key, params[key]);
          }
        });

        const query = queryParams.toString();
        const response = await fetch(`${API_BASE_URL}/admin/activities${query ? '?' + query : ''}`, {
          method: 'GET',
          headers: {
            ...api.getAuthHeaders(),
            'Accept': 'application/json'
          }
        });

        const data = await response.json();
        if (!response.ok) {
          throw new Error(data.message || 'Failed to fetch activities');
        }
        return data;
      } catch (error) {
        console.error('Error fetching admin activities:', error);
        throw error;
      }
    },

    // Beneficiary Group Management
    createBeneficiaryGroup: async (groupData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard/admin/beneficiary-groups`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(groupData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Create beneficiary group error:', error);
        throw error;
      }
    },

    getBeneficiaryGroups: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard/admin/beneficiary-groups`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get beneficiary groups error:', error);
        throw error;
      }
    },

    updateBeneficiaryGroup: async (groupId, groupData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard/admin/beneficiary-groups/${groupId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(groupData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Update beneficiary group error:', error);
        throw error;
      }
    },

    deleteBeneficiaryGroup: async (groupId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard/admin/beneficiary-groups/${groupId}`, {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Delete beneficiary group error:', error);
        throw error;
      }
    },

    useBeneficiaryGroup: async (groupId, fundData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/dashboard/admin/beneficiary-groups/${groupId}/use`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(fundData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Use beneficiary group error:', error);
        throw error;
      }
    },

    // Add more dashboard endpoints as needed
  },
  getAuthHeaders: () => {
    const token = localStorage.getItem('token');
    // console.log(token);
    return {
      'x-auth-token': token,
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    };
  },

  // Notification methods
  notifications: {
    sendNotification: async (notificationData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/send-push-notification`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(notificationData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Send notification error:', error);
        throw error;
      }
    },

    getNotifications: async (page = 1, limit = 10) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/notifications?page=${page}&limit=${limit}`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get notifications error:', error);
        throw error;
      }
    },

    getUnreadCount: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/notifications/unread`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get unread count error:', error);
        throw error;
      }
    },

    markAsViewed: async (notificationId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/notifications/${notificationId}/view`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Mark notification as viewed error:', error);
        throw error;
      }
    },

    markAllAsViewed: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/notifications/view-all`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Mark all notifications as viewed error:', error);
        throw error;
      }
    },

    deleteNotification: async (notificationId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/notifications/${notificationId}`, {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Delete notification error:', error);
        throw error;
      }
    }
  },

  // WhatsApp test method
  testWhatsAppConnection: async (phoneNumber) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/whatsapp/test?phoneNumber=${encodeURIComponent(phoneNumber)}`, {
        method: 'GET',
        headers: api.getAuthHeaders(),
      });
      return handleResponse(response);
    } catch (error) {
      console.error('WhatsApp test error:', error);
      throw error;
    }
  },

  // Smart Notifications endpoints
  smartNotifications: {
    getTemplates: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/templates`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get templates error:', error);
        throw error;
      }
    },

    sendToUser: async (userId, templateKey, customData = {}) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/send-to-user`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ userId, templateKey, customData })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Send to user error:', error);
        throw error;
      }
    },

    analyzeUser: async (userId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/analyze-user`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ userId })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Analyze user error:', error);
        throw error;
      }
    },

    getUserAnalysis: async (userId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/user-analysis/${userId}`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get user analysis error:', error);
        throw error;
      }
    },

    runCampaign: async (campaignType, limit = 100) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/campaign/${campaignType}`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ limit })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Run campaign error:', error);
        throw error;
      }
    },

    sendBulkNotifications: async (userIds = null, limit = 100) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/bulk`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ userIds, limit })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Send bulk notifications error:', error);
        throw error;
      }
    },

    sendTargetedNotifications: async (criteria, templateKey, customData = {}) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/targeted`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ criteria, templateKey, customData })
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Send targeted notifications error:', error);
        throw error;
      }
    },

    runScheduledCampaign: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/schedule/daily`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Run scheduled campaign error:', error);
        throw error;
      }
    },

    // Enhanced analytics and reporting
    getAnalytics: async (filters = {}) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/analytics`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(filters)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get analytics error:', error);
        throw error;
      }
    },

    // Real-time analytics
    getRealTimeStats: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/real-time-stats`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get real-time stats error:', error);
        throw error;
      }
    },

    // A/B Testing endpoints
    createABTest: async (testData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/ab-test`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(testData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Create A/B test error:', error);
        throw error;
      }
    },

    getABTestResults: async (testId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/ab-test/${testId}/results`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get A/B test results error:', error);
        throw error;
      }
    },

    // User segmentation endpoints
    createSegment: async (segmentData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/segments`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(segmentData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Create segment error:', error);
        throw error;
      }
    },

    getSegments: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/segments`, {
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get segments error:', error);
        throw error;
      }
    },

    // Template management endpoints
    createTemplate: async (templateData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/templates`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(templateData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Create template error:', error);
        throw error;
      }
    },

    updateTemplate: async (templateKey, templateData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/templates/${templateKey}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify(templateData)
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Update template error:', error);
        throw error;
      }
    },

    deleteTemplate: async (templateKey) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/smart-notifications/templates/${templateKey}`, {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Delete template error:', error);
        throw error;
      }
    },
    triggerMeterLow: async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.post(
          `${API_BASE_URL}/api/smart-notification/trigger-meter-low`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
        return response.data;
      } catch (error) {
        throw error.response?.data || error.message;
      }
    },
  },

  // Adverts endpoints
  adverts: {
    createAdvert: async (advertData) => {
      try {
        const formData = new FormData();

        // Log the incoming data
        console.log('Advert Data:', advertData);

        // Ensure all required fields are present
        if (!advertData.title || !advertData.description || !advertData.startDate || !advertData.endDate) {
          throw new Error('Please provide all required fields: title, description, startDate, endDate');
        }

        // Add each field to FormData
        formData.append('title', advertData.title);
        formData.append('description', advertData.description);
        formData.append('startDate', new Date(advertData.startDate).toISOString());
        formData.append('endDate', new Date(advertData.endDate).toISOString());

        if (advertData.link) {
          formData.append('link', advertData.link);
        }

        if (advertData.image) {
          formData.append('image', advertData.image);
        }

        // Log the FormData contents
        for (let pair of formData.entries()) {
          console.log(pair[0] + ': ' + pair[1]);
        }

        const response = await fetch(`${API_BASE_URL}/admin/createAdvert`, {
          method: 'POST',
          headers: {
            'Accept': 'application/json',
            'x-auth-token': JSON.parse(localStorage.getItem('user')).token,
          },
          body: formData,
        });

        const data = await response.json();
        console.log('Response:', data);

        if (!response.ok) {
          throw data;
        }
        return data;
      } catch (error) {
        console.error('Create advert error:', error);
        throw error;
      }
    },

    getAllAdverts: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/getAdverts`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Get adverts error:', error);
        throw error;
      }
    },

    updateAdvert: async (advertId, advertData) => {
      try {
        const formData = new FormData();

        // Add each field to FormData
        formData.append('title', advertData.title);
        formData.append('description', advertData.description);
        formData.append('startDate', new Date(advertData.startDate).toISOString());
        formData.append('endDate', new Date(advertData.endDate).toISOString());

        if (advertData.link) {
          formData.append('link', advertData.link);
        }

        if (advertData.image) {
          formData.append('image', advertData.image);
        }

        const response = await fetch(`${API_BASE_URL}/admin/adverts/${advertId}`, {
          method: 'PUT',
          headers: {
            'Accept': 'application/json',
            'x-auth-token': JSON.parse(localStorage.getItem('user')).token,
            // Don't set Content-Type - let the browser set it automatically for FormData
          },
          body: formData,
        });

        const data = await response.json();
        if (!response.ok) {
          throw data;
        }
        return data;
      } catch (error) {
        console.error('Update advert error:', error);
        throw error;
      }
    },

    deleteAdvert: async (advertId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/deleteAdvert/${advertId}`, {
          method: 'DELETE',
          headers: api.getAuthHeaders(),
        });
        return handleResponse(response);
      } catch (error) {
        console.error('Delete advert error:', error);
        throw error;
      }
    },
  },

  // Chat endpoints
  chat: {
    getAllChats: async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/getAllChats`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data || []
        };
      } catch (error) {
        console.error('Get all chats error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    getChatMessages: async (chatId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/chat/${chatId}/messages`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        console.log('Fetched messages:', data);
        return {
          success: true,
          data: data || []
        };
      } catch (error) {
        console.error('Get chat messages error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    sendMessage: async (messageData) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/chat/${messageData.chatId}/messages`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({
            content: messageData.content,
            receiver: messageData.receiver
          }),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data
        };
      } catch (error) {
        console.error('Send message error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    getChatStats: async () => {
      try {
        // Calculate stats from existing chats
        const chatsResponse = await api.chat.getAllChats();
        if (!chatsResponse.success) {
          throw new Error('Failed to get chats');
        }

        const chats = chatsResponse.data;
        const totalChats = chats.length;
        const activeChats = chats.filter(chat => chat.status !== 'resolved').length;
        const resolvedChats = chats.filter(chat => chat.status === 'resolved').length;

        // Calculate average response time (mock data for now)
        const averageResponseTime = Math.floor(Math.random() * 10) + 2; // 2-12 minutes

        return {
          success: true,
          data: {
            totalChats,
            activeChats,
            resolvedChats,
            averageResponseTime
          }
        };
      } catch (error) {
        console.error('Get chat stats error:', error);
        return {
          success: false,
          error: error.message,
          data: {
            totalChats: 0,
            activeChats: 0,
            resolvedChats: 0,
            averageResponseTime: 0
          }
        };
      }
    },

    getAgentChats: async (username) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/getAgentChat/${username}`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data || []
        };
      } catch (error) {
        console.error('Get agent chats error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    updateChatStatus: async (chatId, status) => {
      try {
        const response = await fetch(`${API_BASE_URL}/admin/updateChatStatus/${chatId}`, {
          method: 'PUT',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ status }),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data
        };
      } catch (error) {
        console.error('Update chat status error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    sendMediaMessage: async (messageData) => {
      try {
        const formData = new FormData();
        formData.append('image', messageData.file);
        formData.append('receiver', messageData.receiver);
        formData.append('messageType', messageData.messageType || 'image');

        const response = await fetch(`${API_BASE_URL}/api/admin/chat/${messageData.chatId}/upload-media`, {
          method: 'POST',
          headers: {
            'x-auth-token': JSON.parse(localStorage.getItem('user')).token,
            // Don't set Content-Type - let the browser set it automatically for FormData
          },
          body: formData,
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data
        };
      } catch (error) {
        console.error('Send media message error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Mark messages as read
    markMessagesAsRead: async (chatId, messageIds) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/chat/${chatId}/mark-read`, {
          method: 'POST',
          headers: api.getAuthHeaders(),
          body: JSON.stringify({ messageIds }),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data
        };
      } catch (error) {
        console.error('Mark messages as read error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    },

    // Get read status for messages in a chat
    getReadStatus: async (chatId) => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/admin/chat/${chatId}/read-status`, {
          method: 'GET',
          headers: api.getAuthHeaders(),
        });
        const data = await handleResponse(response);
        return {
          success: true,
          data: data || []
        };
      } catch (error) {
        console.error('Get read status error:', error);
        return {
          success: false,
          error: error.message
        };
      }
    }
  },

};

// Add event service methods to api object
Object.assign(api, { events: eventService });

export default api; 